import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { AppEnv } from '@/lib/env';
import type {
  ChannelLike,
  ConnectionCallback,
  ConnectionState,
  EventCallback,
  RealtimeClientLike,
} from './contract';

/**
 * Production realtime client (V1-05): laravel-echo + pusher-js against the
 * EXISTING Reverb/Pusher-compatible host from env.reverb (06-realtime.md §2).
 * Implements the same RealtimeClientLike surface as the test
 * FakeReverbClient, so RealtimeStore is transport-agnostic.
 *
 * Mirrors pos-app/src/services/websocketService.ts:
 *   - :40-49  Pusher options (wsHost/wsPort/wssPort/forceTLS/enabledTransports/
 *             disableStats/cluster '') — reproduced by buildEchoOptions().
 *   - :90-93  public `orders.{organizationId}` subscription, no auth request.
 *   - :113/:128 events are bound with their BARE names.
 *
 * ⚠ laravel-echo NAMESPACE PITFALL (fake-reverb.ts header): Echo's
 * `.listen('NewOrderReceived')` would bind "App\\Events\\NewOrderReceived",
 * but the current publisher broadcasts the BARE name. Echo's documented
 * bypass is a LEADING DOT — its EventFormatter returns `event.slice(1)`
 * verbatim for dot-prefixed names (laravel-echo dist EventFormatter.format).
 * `toEchoEventName` therefore normalizes EVERY consumer spelling (bare or
 * dotted) to exactly one leading dot before calling Echo, so the wire binding
 * is always the bare event name. Live verification steps are recorded in
 * docs/report/v1-05.md (pending owner credentials).
 *
 * NEVER log the app key, connection URLs, or event payloads (09-quality §6).
 */

type ReverbConfig = NonNullable<AppEnv['reverb']>;

/** Normalize any consumer spelling to Echo's bypass-namespace form (".Event"). */
export function toEchoEventName(event: string): string {
  return `.${event.startsWith('.') ? event.slice(1) : event}`;
}

/**
 * Echo/Pusher options for the existing host. Pure — unit-tested without
 * opening sockets. `cluster` is set by Echo's reverb connector; the key is
 * whatever env provides (there is NO hardcoded fallback — env.reverb is null
 * without it and the console stays in polling mode).
 */
export function buildEchoOptions(config: ReverbConfig) {
  return {
    broadcaster: 'reverb' as const,
    key: config.appKey,
    wsHost: config.host,
    wsPort: config.port,
    wssPort: config.port,
    forceTLS: config.scheme === 'https',
    enabledTransports: ['ws', 'wss'] as ('ws' | 'wss')[],
    disableStats: true,
    /** Echo must never install its global axios/jQuery socket-id interceptors. */
    withoutInterceptors: true,
  };
}

/** The minimal Echo surface this adapter uses (injectable for tests). */
export interface EchoLike {
  channel(name: string): {
    listen(event: string, cb: EventCallback): unknown;
    stopListening(event: string, cb?: EventCallback): unknown;
  };
  leaveChannel(name: string): void;
  disconnect(): void;
  connector: {
    pusher: {
      connection: {
        state: string;
        bind(event: string, cb: ConnectionCallback): unknown;
        unbind(event?: string, cb?: ConnectionCallback): unknown;
      };
    };
  };
}

function createRealEcho(config: ReverbConfig): EchoLike {
  // `Pusher` is passed as an option so no window global is required
  // (laravel-echo PusherConnector prefers options.Pusher over window.Pusher).
  return new Echo<'reverb'>({ ...buildEchoOptions(config), Pusher }) as unknown as EchoLike;
}

class EchoChannel implements ChannelLike {
  readonly name: string;
  private readonly echo: EchoLike;
  private readonly bindings: Array<{ event: string; cb: EventCallback }> = [];

  constructor(echo: EchoLike, name: string) {
    this.echo = echo;
    this.name = name;
  }

  listen(event: string, callback: EventCallback): this {
    this.echo.channel(this.name).listen(toEchoEventName(event), callback);
    this.bindings.push({ event: toEchoEventName(event), cb: callback });
    return this;
  }

  /** pusher-js spelling — same bare-name semantics. */
  bind(event: string, callback: EventCallback): this {
    return this.listen(event, callback);
  }

  stopListening(event: string, callback?: EventCallback): this {
    const key = toEchoEventName(event);
    this.echo.channel(this.name).stopListening(key, callback);
    for (let i = this.bindings.length - 1; i >= 0; i -= 1) {
      const binding = this.bindings[i]!;
      if (binding.event === key && (!callback || binding.cb === callback)) {
        this.bindings.splice(i, 1);
      }
    }
    return this;
  }

  unbind(event?: string, callback?: EventCallback): this {
    if (event === undefined) {
      for (const binding of [...this.bindings]) this.stopListening(binding.event, binding.cb);
      return this;
    }
    return this.stopListening(event, callback);
  }
}

export class EchoClient implements RealtimeClientLike {
  private readonly echo: EchoLike;
  private readonly channels = new Map<string, EchoChannel>();

  readonly connection: RealtimeClientLike['connection'];

  /** `echoOverride` lets tests inject a stub instead of opening a socket. */
  constructor(config: ReverbConfig, echoOverride?: EchoLike) {
    this.echo = echoOverride ?? createRealEcho(config);
    const pusherConnection = this.echo.connector.pusher.connection;
    this.connection = {
      get state(): ConnectionState {
        return pusherConnection.state as ConnectionState;
      },
      bind(event, cb): void {
        pusherConnection.bind(event, cb);
      },
      unbind(event?, cb?): void {
        pusherConnection.unbind(event, cb);
      },
    };
  }

  subscribe(channelName: string): ChannelLike {
    let channel = this.channels.get(channelName);
    if (!channel) {
      // echo.channel() performs the pusher subscribe; wrapper adds bookkeeping.
      this.echo.channel(channelName);
      channel = new EchoChannel(this.echo, channelName);
      this.channels.set(channelName, channel);
    }
    return channel;
  }

  unsubscribe(channelName: string): void {
    this.channels.get(channelName)?.unbind();
    this.channels.delete(channelName);
    this.echo.leaveChannel(channelName);
  }

  disconnect(): void {
    for (const name of [...this.channels.keys()]) this.unsubscribe(name);
    this.echo.disconnect();
  }
}
